import {createSlice, createAsyncThunk, PayloadAction, createSelector} from '@reduxjs/toolkit';
import {IntercomDevice, IntercomDeviceStatusEnum} from 'src/apis/autoGenerate/types/intercomDevice';
import {RootState, store} from '../store';
import {intercomDeviceServiceFind, apartmentServiceFind} from 'src/apis/autoGenerate/services';

export interface DevicesState {
  deviceList: IntercomDevice[];
  loadingList: boolean;
  totalDevices: number;
  onlineDevices: number;
  lastStatsFetched: number | null;
  loadingStats: boolean;
}

const initialState: DevicesState = {
  deviceList: [],
  loadingList: false,
  totalDevices: 0,
  onlineDevices: 0,
  lastStatsFetched: null,
  loadingStats: false,
};

export const fetchDeviceStats = createAsyncThunk(
  'device/fetchStats',
  async ({orgId, userId}: {orgId: string; userId?: string}) => {
    try {
      // First, get user's accessible apartments
      const apartmentsRes = await apartmentServiceFind(orgId, undefined, undefined, userId);
      const apartments = apartmentsRes?.data?.apartments || [];

      if (apartments.length === 0) {
        return {
          totalDevices: 0,
          onlineDevices: 0,
          devices: [],
        };
      }

      // Get apartment IDs
      const apartmentIds = apartments.map(apt => apt.id).filter(Boolean) as string[];

      // Fetch devices from accessible apartments only
      const response = await intercomDeviceServiceFind(orgId, undefined, undefined, apartmentIds);

      const devices = response.data.intercomDevices || [];
      const totalDevices = devices.length;

      const onlineDevices = devices.filter(
        device => device.status === IntercomDeviceStatusEnum.Online
      ).length;

      return {
        totalDevices,
        onlineDevices,
        devices,
      };
    } catch (error) {
      console.error('Error fetching device statistics:', error);
      return {
        totalDevices: 0,
        onlineDevices: 0,
        devices: [],
      };
    }
  }
);

export const deviceSlice = createSlice({
  name: 'device',
  initialState,
  reducers: {
    setDeviceList: (state, action: PayloadAction<IntercomDevice[]>) => {
      state.deviceList = action.payload;
      state.totalDevices = action.payload.length;
      state.onlineDevices = action.payload.filter(
        device => device.status === IntercomDeviceStatusEnum.Online
      ).length;
      state.lastStatsFetched = Date.now();
    },
    setLoadingList: (state, action: PayloadAction<boolean>) => {
      state.loadingList = action.payload;
    },
    setLoadingStats: (state, action: PayloadAction<boolean>) => {
      state.loadingStats = action.payload;
    },
    setDeviceStats: (
      state,
      action: PayloadAction<{totalDevices: number; onlineDevices: number}>
    ) => {
      state.totalDevices = action.payload.totalDevices;
      state.onlineDevices = action.payload.onlineDevices;
      state.lastStatsFetched = Date.now();
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchDeviceStats.pending, state => {
        state.loadingStats = true;
      })
      .addCase(fetchDeviceStats.fulfilled, (state, action) => {
        state.totalDevices = action.payload.totalDevices;
        state.onlineDevices = action.payload.onlineDevices;
        state.deviceList = action.payload.devices;
        state.lastStatsFetched = Date.now();
        state.loadingStats = false;
      })
      .addCase(fetchDeviceStats.rejected, state => {
        state.loadingStats = false;
      });
  },
});

export const {setDeviceList, setLoadingList, setLoadingStats, setDeviceStats} = deviceSlice.actions;
export const selectDevice = (state: RootState) => state.device;

// 使用 createSelector 来记忆化 selector，避免不必要的重新渲染
export const selectDeviceStats = createSelector([(state: RootState) => state.device], device => ({
  totalDevices: device?.totalDevices || 0,
  onlineDevices: device?.onlineDevices || 0,
}));

export const selectDeviceStatsLastFetched = (state: RootState) => state.device?.lastStatsFetched;
export const selectLoadingStats = (state: RootState) => state.device?.loadingStats || false;

// 新增 selector
export const selectDeviceById = (state: RootState, deviceId: string) =>
  state.device?.deviceList?.find(device => device.id === deviceId);

export const selectDeviceTimeZone = (state: RootState, deviceId: string) =>
  state.device?.deviceList?.find(device => device.id === deviceId)?.timeZone;

export const selectAllDeviceTimeZones = (state: RootState) =>
  state.device?.deviceList?.map(device => ({
    id: device.id,
    name: device.name,
    timeZone: device.timeZone,
  })) || [];

export async function getDeviceList(orgId?: string, userId?: string, forceGet = false) {
  const state = store.getState();
  if (state.device?.deviceList?.length === 0 || forceGet) {
    store.dispatch(setLoadingList(true));

    try {
      // Get user's accessible apartments first
      if (orgId) {
        const apartmentsRes = await apartmentServiceFind(orgId, undefined, undefined, userId);
        const apartments = apartmentsRes?.data?.apartments || [];

        if (apartments.length === 0) {
          store.dispatch(setDeviceList([]));
          return [];
        }

        const apartmentIds = apartments.map(apt => apt.id).filter(Boolean) as string[];

        // Fetch devices filtered by apartment IDs
        const res = await intercomDeviceServiceFind(orgId, undefined, undefined, apartmentIds);
        const devices = (res.data.intercomDevices || []) as IntercomDevice[];
        store.dispatch(setDeviceList(devices));
        return devices;
      } else {
        // Fallback: fetch without filtering (for backward compatibility, but not recommended)
        const res = await intercomDeviceServiceFind();
        const devices = (res.data.intercomDevices || []) as IntercomDevice[];
        store.dispatch(setDeviceList(devices));
        return devices;
      }
    } finally {
      store.dispatch(setLoadingList(false));
    }
  }
  return state.device?.deviceList || [];
}

export async function getDeviceById(id: string) {
  await getDeviceList();
  const state = store.getState();
  return state.device?.deviceList?.find((device: IntercomDevice) => device.id === id);
}

export default deviceSlice.reducer;
